import { AppShell } from '@app/layouts/AppShell';
import { features } from '@config/features';
import { ProtectedRoute } from '@features/auth/components/ProtectedRoute';
import { PublicOnlyRoute } from '@features/auth/components/PublicOnlyRoute';
import { useAuth } from '@features/auth/hooks/useAuth';
import { Role } from '@features/auth/types';
import { FeatureBoundary } from '@shared/components/feedback/FeatureBoundary';
import { ForbiddenPage } from '@shared/components/feedback/ForbiddenPage';
import { FullPageProgress } from '@shared/components/feedback/FullPageProgress';
import { NotFoundPage } from '@shared/components/feedback/NotFoundPage';
import { useIsPhone } from '@shared/hooks/useIsPhone';
import { lazyWithRetry } from '@shared/utils/lazyWithRetry';
import { type ReactNode, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import type { RouteHandle } from './handle';
import { homePathForRoles } from './homePath';
import { paths } from './paths';

/** Role-aware landing: admins → dashboard, tenants → customer list. */
function HomeRedirect() {
  const { hasRole } = useAuth();
  const isPhone = useIsPhone();
  return <Navigate to={homePathForRoles(hasRole, { isPhone })} replace />;
}

const LoginPage = lazyWithRetry(
  () => import('@features/auth/pages/LoginPage').then((m) => ({ default: m.LoginPage })),
  'LoginPage',
);
const ForgotPasswordPage = lazyWithRetry(
  () =>
    import('@features/auth/pages/ForgotPasswordPage').then((m) => ({
      default: m.ForgotPasswordPage,
    })),
  'ForgotPasswordPage',
);
const ResetPasswordPage = lazyWithRetry(
  () =>
    import('@features/auth/pages/ResetPasswordPage').then((m) => ({
      default: m.ResetPasswordPage,
    })),
  'ResetPasswordPage',
);
const DashboardPage = lazyWithRetry(
  () => import('@features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
  'DashboardPage',
);
const ProfilePage = lazyWithRetry(
  () => import('@features/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })),
  'ProfilePage',
);
const UsersPage = lazyWithRetry(
  () => import('@features/users/UsersPage').then((m) => ({ default: m.UsersPage })),
  'UsersPage',
);
const ClientsPage = lazyWithRetry(
  () => import('@features/clients/ClientsPage').then((m) => ({ default: m.ClientsPage })),
  'ClientsPage',
);
const ProjectClientsPage = lazyWithRetry(
  () =>
    import('@features/projectClients/ProjectClientsPage').then((m) => ({
      default: m.ProjectClientsPage,
    })),
  'ProjectClientsPage',
);
const PartnersPage = lazyWithRetry(
  () => import('@features/partners/PartnersPage').then((m) => ({ default: m.PartnersPage })),
  'PartnersPage',
);
const PricingPackagesPage = lazyWithRetry(
  () =>
    import('@features/pricingPackages/PricingPackagesPage').then((m) => ({
      default: m.PricingPackagesPage,
    })),
  'PricingPackagesPage',
);
const ClientDetailsPage = lazyWithRetry(
  () =>
    import('@features/clients/ClientDetailsPage').then((m) => ({
      default: m.ClientDetailsPage,
    })),
  'ClientDetailsPage',
);
const ProjectsPage = lazyWithRetry(
  () => import('@features/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage })),
  'ProjectsPage',
);
const ProjectPlanPage = lazyWithRetry(
  () =>
    import('@features/projects/ProjectPlanPage').then((m) => ({
      default: m.ProjectPlanPage,
    })),
  'ProjectPlanPage',
);
// Lazy keeps the ~8k LOC catalog data out of the main bundle.
const PriceCatalogPage = lazyWithRetry(
  () =>
    import('@features/priceCatalog/pages/PriceCatalogPage').then((m) => ({
      default: m.PriceCatalogPage,
    })),
  'PriceCatalogPage',
);
// Lazy: the page runs the Herholz door-card logic for its template editor.
const DoorTemplatesPage = lazyWithRetry(
  () =>
    import('@features/doorTemplates/pages/DoorTemplatesPage').then((m) => ({
      default: m.DoorTemplatesPage,
    })),
  'DoorTemplatesPage',
);
const DoorRulesPage = lazyWithRetry(
  () =>
    import('@features/doorTemplates/pages/DoorRulesPage').then((m) => ({
      default: m.DoorRulesPage,
    })),
  'DoorRulesPage',
);
const EvaluationFloorPlanPage = lazyWithRetry(
  () =>
    import('@features/floor-plan-mvp/EvaluationFloorPlanPage').then((m) => ({
      default: m.EvaluationFloorPlanPage,
    })),
  'EvaluationFloorPlanPage',
);
const OfferEditorPage = lazyWithRetry(
  () =>
    import('@features/offer/pages/OfferEditorPage').then((m) => ({
      default: m.OfferEditorPage,
    })),
  'OfferEditorPage',
);

function withSuspense(element: ReactNode, feature?: string): ReactNode {
  // Each feature route gets its own boundary so a single screen crashing
  // shows an inline alert instead of replacing the whole shell.
  return (
    <FeatureBoundary feature={feature}>
      <Suspense fallback={<FullPageProgress />}>{element}</Suspense>
    </FeatureBoundary>
  );
}

const handles = {
  login: { titleKey: 'routes.login', breadcrumbKey: null } satisfies RouteHandle,
  forgot: { titleKey: 'routes.forgotPassword', breadcrumbKey: null } satisfies RouteHandle,
  reset: { titleKey: 'routes.resetPassword', breadcrumbKey: null } satisfies RouteHandle,
  forbidden: { titleKey: 'routes.forbidden', breadcrumbKey: null } satisfies RouteHandle,
  shell: { breadcrumbKey: null } satisfies RouteHandle,
  dashboard: { titleKey: 'routes.dashboard' } satisfies RouteHandle,
  profile: { titleKey: 'routes.profile', mobileReady: true } satisfies RouteHandle,
  users: { titleKey: 'routes.users' } satisfies RouteHandle,
  clients: { titleKey: 'routes.clients' } satisfies RouteHandle,
  projectClients: { titleKey: 'routes.clients' } satisfies RouteHandle,
  partners: { titleKey: 'routes.partners' } satisfies RouteHandle,
  pricingPackages: { titleKey: 'routes.pricingPackages' } satisfies RouteHandle,
  clientDetails: { titleKey: 'routes.clientDetails' } satisfies RouteHandle,
  projects: { titleKey: 'routes.projects', breadcrumbKey: null } satisfies RouteHandle,
  projectWorkspace: {
    titleKey: 'routes.projectWorkspace',
    breadcrumbKey: null,
  } satisfies RouteHandle,
  evaluationPlan: { titleKey: 'routes.evaluationPlan' } satisfies RouteHandle,
  evaluationOffer: { titleKey: 'routes.offerEditor' } satisfies RouteHandle,
  // The one page built for phones: the rest of the shell is desktop-only there.
  priceCatalog: { titleKey: 'routes.priceCatalog', mobileReady: true } satisfies RouteHandle,
  doorTemplates: { titleKey: 'routes.doorTemplates' } satisfies RouteHandle,
  doorRules: { titleKey: 'routes.doorRules' } satisfies RouteHandle,
} as const;

export const router = createBrowserRouter([
  {
    path: paths.login,
    element: <PublicOnlyRoute>{withSuspense(<LoginPage />, 'auth.login')}</PublicOnlyRoute>,
    handle: handles.login,
  },
  {
    path: paths.forgotPassword,
    element: withSuspense(<ForgotPasswordPage />, 'auth.forgotPassword'),
    handle: handles.forgot,
  },
  {
    path: paths.resetPassword,
    element: withSuspense(<ResetPasswordPage />, 'auth.resetPassword'),
    handle: handles.reset,
  },
  {
    path: paths.forbidden,
    element: <ForbiddenPage />,
    handle: handles.forbidden,
  },
  {
    path: paths.root,
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    handle: handles.shell,
    children: [
      // Only redirects, so it must run on a phone too (it sends tenants to the catalog there).
      {
        index: true,
        element: <HomeRedirect />,
        handle: { mobileReady: true } satisfies RouteHandle,
      },
      {
        path: paths.dashboard.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Admin]}>
            {withSuspense(<DashboardPage />, 'dashboard')}
          </ProtectedRoute>
        ),
        handle: handles.dashboard,
      },
      {
        path: paths.profile.slice(1),
        element: withSuspense(<ProfilePage />, 'profile'),
        handle: handles.profile,
      },
      {
        path: paths.users.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Admin]}>
            {withSuspense(<UsersPage />, 'users')}
          </ProtectedRoute>
        ),
        handle: handles.users,
      },
      {
        path: paths.clients.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Admin]}>
            {withSuspense(<ClientsPage />, 'clients')}
          </ProtectedRoute>
        ),
        handle: handles.clients,
      },
      {
        path: paths.projectClients.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Tenant]}>
            {withSuspense(<ProjectClientsPage />, 'projectClients')}
          </ProtectedRoute>
        ),
        handle: handles.projectClients,
      },
      {
        path: paths.partners.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Admin]}>
            {withSuspense(<PartnersPage />, 'partners')}
          </ProtectedRoute>
        ),
        handle: handles.partners,
      },
      {
        path: paths.pricingPackages.slice(1),
        element: features.pricingPackages ? (
          <ProtectedRoute requiredRoles={[Role.Admin, Role.Tenant]}>
            {withSuspense(<PricingPackagesPage />, 'pricingPackages')}
          </ProtectedRoute>
        ) : (
          // Switched off: old links and bookmarks land on the home page, not on a dead end.
          <Navigate to={paths.root} replace />
        ),
        handle: handles.pricingPackages,
      },
      {
        path: paths.clientDetails.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Admin]}>
            {withSuspense(<ClientDetailsPage />, 'clients.details')}
          </ProtectedRoute>
        ),
        handle: handles.clientDetails,
      },
      {
        path: paths.priceCatalogCategory.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Tenant]}>
            {withSuspense(<PriceCatalogPage />, 'priceCatalog')}
          </ProtectedRoute>
        ),
        handle: handles.priceCatalog,
      },
      {
        path: paths.doorTemplates.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Tenant]}>
            {withSuspense(<DoorTemplatesPage />, 'doorTemplates')}
          </ProtectedRoute>
        ),
        handle: handles.doorTemplates,
      },
      {
        path: paths.doorRules.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Tenant]}>
            {withSuspense(<DoorRulesPage />, 'doorRules')}
          </ProtectedRoute>
        ),
        handle: handles.doorRules,
      },
      {
        path: paths.projectClientProjects.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Tenant]}>
            {withSuspense(<ProjectsPage />, 'projects')}
          </ProtectedRoute>
        ),
        handle: handles.projects,
      },
      {
        path: paths.projectWorkspace.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Tenant]}>
            {withSuspense(<ProjectPlanPage />, 'projects.plan')}
          </ProtectedRoute>
        ),
        handle: handles.projectWorkspace,
      },
      {
        path: paths.projectWorkspaceEvaluationPlan.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Tenant]}>
            {withSuspense(<EvaluationFloorPlanPage />, 'evaluation-plan')}
          </ProtectedRoute>
        ),
        handle: handles.evaluationPlan,
      },
      {
        path: paths.evaluationPlan.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Tenant]}>
            {withSuspense(<EvaluationFloorPlanPage />, 'evaluation-plan')}
          </ProtectedRoute>
        ),
        handle: handles.evaluationPlan,
      },
      {
        path: paths.projectWorkspaceEvaluationOffer.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Tenant]}>
            {withSuspense(<OfferEditorPage />, 'offer')}
          </ProtectedRoute>
        ),
        handle: handles.evaluationOffer,
      },
      {
        path: paths.evaluationOffer.slice(1),
        element: (
          <ProtectedRoute requiredRoles={[Role.Tenant]}>
            {withSuspense(<OfferEditorPage />, 'offer')}
          </ProtectedRoute>
        ),
        handle: handles.evaluationOffer,
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
